import React from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { AssetRopaCapabilities } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';
import DpiaAvailableIndicator from 'pages/ProcessingActivities/_shared/DpiaAvailableIndicator';
import DpiaRowContextMenu from 'pages/ProcessingActivities/_shared/DpiaRowContextMenu';
import RopaCountryChips from 'pages/ProcessingActivities/_shared/RopaCountryChips';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

import {
  formatProcessingActivityReviewDate,
  getDpiaBadgeClass,
  resolveApaDpiaDisplayLabel,
  resolveDpiaBadgeStatus,
  getReviewStatusDotClass,
  getUniqueRowMenuItems,
} from './assetProcessingActivityReview.helpers';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

type Props = {
  capabilities: AssetRopaCapabilities;
  emptyStateContent?: ReactNodeLike;
  onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
  onRemove?: (row: AssetProcessingActivityReviewRow) => void;
  rows: AssetProcessingActivityReviewRow[];
};

const AssetProcessingActivityReviewBoxList: React.FC<Props> = ({
  capabilities,
  emptyStateContent,
  onAssessDpia,
  onOpenAdminDpiaReview,
  onOpenAdminReview,
  onOpenOwnerEditor,
  onRemove,
  rows,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const showActionsMenu =
    capabilities.canOpenAdminReviewPage ||
    (capabilities.canOpenApaEditor && capabilities.canEditApa) ||
    capabilities.canRemoveProcessingActivity ||
    capabilities.canAssessDpia ||
    capabilities.canOpenAdminDpiaReview;

  const handleRowClick = (row: AssetProcessingActivityReviewRow) => {
    if (capabilities.canOpenAdminReviewPage) {
      onOpenAdminReview(row);
      return;
    }

    if (capabilities.canOpenApaEditor) {
      onOpenOwnerEditor(row, 'view');
    }
  };

  const handleRowKeyDown = (event: React.KeyboardEvent, row: AssetProcessingActivityReviewRow) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowClick(row);
    }
  };

  if (rows.length === 0) {
    return (
      <div
        aria-label={t(processing_activities_messages.asset_ropa.table.aria_label)}
        className={clsx(styles.processingActivityReviewList, styles.processingActivityReviewEmptyState)}
        role='region'
      >
        {emptyStateContent}
      </div>
    );
  }

  return (
    <div
      aria-label={t(processing_activities_messages.asset_ropa.table.aria_label)}
      className={styles.processingActivityReviewList}
      role='region'
    >
      {rows.map(row => {
        const menuItems = showActionsMenu
          ? getUniqueRowMenuItems(
              row,
              capabilities,
              {
                onAssessDpia,
                onOpenAdminDpiaReview,
                onOpenOwnerEditor: (activityRow, mode) =>
                  capabilities.canOpenAdminReviewPage && mode === 'view'
                    ? onOpenAdminReview(activityRow)
                    : onOpenOwnerEditor(activityRow, mode),
                onRemove,
              },
              t,
              false,
            )
          : [];

        return (
          <div
            key={row.id}
            aria-label={t(processing_activities_messages.asset_ropa.table.row_aria_label, { name: row.processingActivityName })}
            className={styles.processingActivityReviewBox}
            onClick={() => handleRowClick(row)}
            onKeyDown={event => handleRowKeyDown(event, row)}
            role='button'
            tabIndex={0}
          >
            <div className={styles.processingActivityReviewBoxHeader}>
              <div className={styles.processingActivityReviewBoxName}>{row.processingActivityName}</div>
              {(menuItems.length > 0 || capabilities.canAssessDpia || capabilities.canOpenAdminDpiaReview) && (
                <div
                  className={styles.processingActivityReviewBoxMenu}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                  role='presentation'
                >
                  <DpiaRowContextMenu
                    apaId={row.assetProcessingActivityId}
                    canAssess={capabilities.canAssessDpia}
                    canOpenDpia={capabilities.canOpenAdminDpiaReview}
                    elements={menuItems}
                    onAssess={() => onAssessDpia(row)}
                    requestStatus={row.dpiaRequestStatus}
                    status={row.dpiaDisplayStatus}
                  >
                    <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.row_menu)} size='small'>
                      <MoreIcon />
                    </IconButton>
                  </DpiaRowContextMenu>
                </div>
              )}
            </div>
            {row.completenessStatus && (
              <div>{t(processing_activities_messages.asset_ropa.owner_workspace.completeness_status[row.completenessStatus])}</div>
            )}
            <div className={styles.workspaceSummaryGrid}>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.owner_workspace.table.countries)}</div>
                <div className={styles.summaryValue}>
                  <RopaCountryChips countriesSummary={row.countriesSummary} />
                </div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.review_status)}</div>
                <div className={clsx(styles.summaryValue, styles.statusCell)}>
                  <span className={clsx(styles.statusDot, getReviewStatusDotClass(row.reviewStatus, styles))} />
                  {row.isHistoricalReview && `${t(processing_activities_messages.asset_ropa.review_status.previous_review)}: `}
                  {t(processing_activities_messages.asset_ropa.review_status[row.reviewStatus as ReviewStatusMessageKey])}
                  {row.changedSinceReview && (
                    <span> · {t(processing_activities_messages.asset_ropa.review_status.changed_since_review)}</span>
                  )}
                </div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.active_purposes)}</div>
                <div className={styles.summaryValue}>{row.activePurposesCount}</div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.dpia_status)}</div>
                <div className={styles.summaryValue}>
                  <span
                    className={clsx(
                      styles.dpiaBadge,
                      getDpiaBadgeClass(
                        resolveDpiaBadgeStatus(row.dpiaDisplayStatus, { dpiaRequestStatus: row.dpiaRequestStatus }),
                        styles,
                      ),
                    )}
                  >
                    {resolveApaDpiaDisplayLabel(row.dpiaDisplayStatus, t, { dpiaRequestStatus: row.dpiaRequestStatus })}
                  </span>
                  <DpiaAvailableIndicator available={row.hasCompletedDpia} />
                </div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.last_updated)}</div>
                <div className={styles.summaryValue}>{formatProcessingActivityReviewDate(row.updatedAt)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssetProcessingActivityReviewBoxList;
